import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, first, tap } from 'rxjs/operators';

import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { DomainProfileService } from 'src/app/infrastructure/services/domain-profile-services/domain-profile.service';
import { IImageDto } from 'src/assets/common/models/image-dto.g';
import { ILoginDto } from 'src/assets/common/models/login-dto.g';
import { IRegisterDto } from 'src/assets/common/models/register-dto.g';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { parseJwt } from '../../shared/storage/functions/parse-jwt';
import { UserStorageService } from '../../shared/storage/services/storage-services/user-storage-service';
import { _FormModelService } from 'src/app/infrastructure/services/model-services/_form-model.service';
import { _LevelGlobalPageService } from 'src/app/infrastructure/services/page-services/_level-global-page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { pageName } from 'src/assets/common/enums/page-name.g';

@Injectable({
    providedIn: 'root'
})
export class AccountPageService
    extends _LevelGlobalPageService
{
    private readonly __userStorageService = injectRequired(UserStorageService);
    private readonly __authService = injectRequired(AuthService);
    private readonly __domainProfileService = injectRequired(DomainProfileService);
    private readonly __applicationUserService = inject(ApplicationUserService, {optional: true});
    private readonly __modelService = inject(_FormModelService, {optional: true});
    photo: IImageDto | null = null;

    constructor() {
        super(
            null,
        )
    }

    override pageCreate_goto = this._createPage_goto;

    override pageRoute_goto(_item?: any): void {
        this.page_goto(pageName.home);
    }

    clearAccount() {
        if(this.__modelService) {
            this.__modelService.dtoModel_set(null);
        }
        this.__applicationUserService?.claims_reset();
        this.__authService.revokeToken();
    }

    register$(data: IRegisterDto): Observable<any> {
        // Geen auto-login: het account moet eerst per mail bevestigd worden
        // (login is server-side geblokkeerd tot EmailConfirmed).
        return this.__authService.postRegister$(data);
    }

    confirm$(email: string, code: string): Observable<any> {
        return this.__authService.postConfirm$(email, code);
    }

    resendConfirm$(email: string): Observable<any> {
        return this.__authService.postResendConfirm$(email);
    }

    login$(data: ILoginDto): Observable<any> {
        return this.__authService.requestToken$(data)
            .pipe(
                tap(resp => {
                    // 2FA-ingeschakeld account: nog geen token, dus nog geen navigatie — dat
                    // gebeurt pas ná verifyTwoFactor$ met een geldige code.
                    if (resp?.requiresTwoFactor) return;

                    this.__navigateAfterLogin(resp);
                }),
                )
    }

    verifyTwoFactor$(pendingToken: string, code: string): Observable<any> {
        return this.__authService.postVerifyTwoFactor$(pendingToken, code)
            .pipe(
                tap(resp => this.__navigateAfterLogin(resp)),
                )
    }

    private __navigateAfterLogin(resp: any): void {
        // Directe read op de guid uit de verse token i.p.v. UserStorageService.pageStart$$():
        // die laatste blijft na een account-wissel in dezelfde sessie (logout -> login als
        // andere user) nog even de vórige gebruiker tonen totdat de asynchrone
        // scope-hydratie-effect is gevlushed — met pageStart$$() zou de startpagina dan een
        // dauGuid van een domain kunnen bevatten waar deze user geen lid van is.
        const _userGuid = resp?.token ? parseJwt(resp.token)?.nameid ?? null : null;
        const _startPage = _userGuid ? this.__userStorageService?.pageStart_forGuid(_userGuid) : null;
        const _returnPage = this.pageReturn$$();
        const _destination = _startPage ?? _returnPage ?? this.pageHome$$();

        // Alleen resetten vóór de navigatie als de bestemming zelf géén domain-context heeft
        // (Home/Account/Login hebben sowieso nooit domain-context, dus dat is altijd correct — de
        // resolver zou toch hetzelfde zetten). Gaat de navigatie WEL naar een domain-pagina (bv.
        // opnieuw inloggen in hetzelfde domain), dan NIET vast naar null zetten: het Login-scherm en
        // de header delen dezelfde DomainProfileService-databron, en de header zit buiten de
        // router-outlet (dus niet resolver-gated) — een tussentijdse null zou daar tot de
        // bestemmingspagina's eigen resolver klaar is het generieke app-logo tonen, óók wanneer je
        // gewoon in hetzelfde domain terugkomt. De bestemmingspagina's resolver zet het (mogelijk
        // identieke) juiste profiel toch vanzelf.
        if (!_destination?.routeKeyData?.dauGuid) {
            this.__domainProfileService.domainProfile_set(null);
        }

        const _homePath = this.pageHome$$()?.routePath;
        const _destinationPath = _destination?.routePath ?? _homePath;
        if (!_destinationPath || _destinationPath === _homePath) {
            this.page_goto(_homePath);
            return;
        }

        // De startpagina kan inmiddels geweigerd worden (bv. domain vereist 2FA, of geen toegang
        // meer): de resolver annuleert dan de navigatie (modelResolve$) en je bleef op Login staan.
        // In dat geval naar Home; de foutmelding van de backend is dan al centraal getoond.
        this._router.navigateByUrl(_destinationPath)
            .then(navigated => { if (!navigated) this.page_goto(_homePath); })
            .catch(() => this.page_goto(_homePath));
    }

    logOut() {
        const logout$ = this.__authService.Token
            ? this.__authService.revokeToken$().pipe(first(), catchError(() => of([])))
            : of([]);

        logout$.pipe(
            finalize(() => {
                this.clearAccount();
                this.page_goto(pageName.login);

                LogService.envDev.logString('logout');
            })
        ).subscribe();
    }

}
